import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Award, BookOpen, GraduationCap, Landmark } from "lucide-react";
import { assetUrl } from "@/lib/constants";
import { Bindi, Reveal, SectionHeading } from "@/components/motifs";
import { ImageLightbox } from "@/components/Lightbox";
import { CREDENTIALS as CREDENTIAL_TEXT } from "@/content/chapters";

const CREDENTIAL_ICONS = {
  "25+ Years With Indian Folk Art": Landmark,
  "Licensed Artisan": Award,
  "Art Educator": GraduationCap,
  "Mithila Art Practitioner": BookOpen,
};
const CREDENTIALS = CREDENTIAL_TEXT.map((c) => ({ ...c, icon: CREDENTIAL_ICONS[c.title] }));

export const RecognitionChapter = ({ honours = [], headingAs }) => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="recognition" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="recognition-section">
      <SectionHeading number="05" title="Recognition" note="Credentials, as documented" as={headingAs} />
      <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2">
        {CREDENTIALS.map((c, i) => (
          <Reveal key={c.title} delay={0.06 * i} className="bg-background">
            <div className="group flex h-full gap-6 p-8 transition-colors duration-500 hover:bg-paper sm:p-10">
              <c.icon
                size={26}
                strokeWidth={1.4}
                className="mt-1 shrink-0 text-foreground/60 transition-colors duration-500 group-hover:text-accent"
              />
              <div>
                <p className="eyebrow text-accent">{c.label}</p>
                <h3 className="mt-3 font-serif text-xl font-medium tracking-tight sm:text-2xl">{c.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">{c.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {honours.length > 0 && (
        <div className="mt-16">
          <Reveal>
            <p className="eyebrow mb-8 flex items-center gap-3 text-muted-foreground">
              <Bindi size={7} /> Honours & recognitions
            </p>
          </Reveal>
          <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {honours.map((h, i) => (
              <Reveal key={h.id} delay={0.05 * i} className="bg-background">
                <div className="group flex h-full flex-col transition-colors duration-500 hover:bg-paper" data-testid={`honour-card-${i}`}>
                  {h.image && (
                    <button
                      onClick={() => setLightbox({ images: [h.image, ...(h.images || [])], index: 0, title: h.title })}
                      className="block overflow-hidden border-b border-border"
                      aria-label={`View certificate for ${h.title}`}
                      data-testid={`honour-image-${i}`}
                    >
                      <img
                        src={assetUrl(h.image)}
                        alt={h.title}
                        loading="lazy"
                        className="h-40 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </button>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                      {[h.event_type, h.date_text].filter(Boolean).join(" · ")}
                    </p>
                    <h3 className="mt-3 font-serif text-lg font-medium leading-snug tracking-tight">{h.title}</h3>
                    {[h.venue, h.city].filter(Boolean).length > 0 && (
                      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {[h.venue, h.city].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {h.description && (
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{h.description}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <Reveal delay={0.2}>
        <p className="mt-8 max-w-2xl font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-muted-foreground">
          My professional experience, teaching and current Mithila practice are presented as
          distinct threads, exactly as I have documented them.
        </p>
      </Reveal>

      <AnimatePresence>
        {lightbox && (
          <ImageLightbox
            images={lightbox.images}
            title={lightbox.title}
            index={lightbox.index}
            onClose={() => setLightbox(null)}
            onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
